import { formatMoney } from '@theme/utilities';
import { QuantityBreaksUpdatedEvent, ThemeEvents } from '@theme/events';

/**
 * Sélecteur de quantité Coalition.
 *
 * L'offre (paliers ou bogo) vient du JSON `cq-config`, copie de la config de la remise maison :
 * les prix affichés suivent exactement le calcul de la Shopify Function (extensions/coalition-remises).
 *
 * Expose l'interface de <quantity-breaks> utilisée par product-form.js et subscription.js :
 * getSelectedItems, getSelectedQuantity, getPrices, getDiscount, getCrossSellPrices,
 * isVariantSelectorActivated.
 */
class CoalitionQuantite extends HTMLElement {
  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-ref="cq-config"]').textContent);
    this.variants = JSON.parse(this.querySelector('[data-ref="cq-variants"]').textContent);
    this.cards = [...this.querySelectorAll('[data-ref="cq-card"]')];
    this.flavors = this.querySelector('[data-ref="cq-flavors"]');
    this.rows = [...this.querySelectorAll('[data-ref="cq-flavor-row"]')];
    this.selects = this.rows.map((row) => row.querySelector('select'));
    this.unit = this.dataset.unit;
    this.units = this.dataset.units;
    this.monthsPerUnit = parseInt(this.dataset.monthsPerUnit, 10) || 0;
    this.dosesPerUnit = parseInt(this.dataset.dosesPerUnit, 10) || 0;

    this.plusCard = this.cards.find((card) => card.hasAttribute('data-plus')) ?? null;
    this.plusMin = this.plusCard ? parseInt(this.plusCard.dataset.quantity, 10) : Infinity;
    this.counters = this.querySelector('[data-ref="cq-counters"]');
    // Parfum de chaque pot (identifiants de variantes) : source unique pour menus, compteurs et panier.
    this.picks = Array(this.#cardQuantity(this.getSelectedQuantity())).fill(this.selects[0].value);

    this.cards.forEach((card) =>
      card.querySelector('input').addEventListener('change', () => this.#onCardChange()),
    );
    this.selects.forEach((select, i) =>
      select.addEventListener('change', () => {
        this.picks[i] = select.value;
        this.#onChange();
      }),
    );
    this.querySelector('[data-ref="cq-same"]')?.addEventListener('click', () => this.#sameFlavor());
    this.counters?.querySelectorAll('[data-ref="cq-counter"]').forEach((row) => {
      const id = row.dataset.variantId;
      row.querySelector('[data-ref="cq-plus-btn"]').addEventListener('click', () => this.#addPot(id));
      row.querySelector('[data-ref="cq-minus"]').addEventListener('click', () => this.#removePot(id));
    });

    this.onVariantUpdated = (event) => this.#onVariantUpdated(event);
    this.onSubscriptionUpdated = () => this.#render();
    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    document.addEventListener(ThemeEvents.subscriptionUpdated, this.onSubscriptionUpdated);

    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    document.removeEventListener(ThemeEvents.subscriptionUpdated, this.onSubscriptionUpdated);
  }

  /* ---------- Interface <quantity-breaks> ---------- */

  /** Toujours vrai : product-form.js prend alors uniquement nos lignes (un parfum par pot). */
  isVariantSelectorActivated() {
    return true;
  }

  getSelectedQuantity() {
    return this.cards.find((card) => card.querySelector('input').checked) ?? this.cards[0];
  }

  /** Une ligne par pot avec son parfum ; product-form.js fusionne les identiques. */
  getSelectedItems() {
    const sellingPlan = this.#sellingPlan();
    return this.#selectedVariants().map((variant) => ({
      id: String(variant.id),
      quantity: 1,
      ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
    }));
  }

  /** Prix achat unique, remise quantité incluse (centimes). */
  getPrices() {
    const { total, compare } = this.#pricing(this.#selectedQuantity());
    return { price: total, compareAtPrice: compare };
  }

  /**
   * Remise que subscription.js (carte abonnement) et product-form.js (prix si abonnement coché)
   * appliquent sur le prix abonnement. Achat unique : déjà incluse dans getPrices.
   */
  getDiscount() {
    const none = { type: 'percentage', value: 0, percentage: 0 };
    // Retournée même quand l'achat unique est coché : la carte abonnement affiche alors le cumul.
    if (!this.config.abonnement) return none;
    // Montant tel que « prix abonnement calculé par le thème (sur le total) − montant retiré » donne
    // exactement le prix Shopify (abonnement puis remise, calculés pot par pot).
    const q = this.#selectedQuantity();
    const { total } = this.#pricing(q, true);
    const oneTime = this.#selectedVariants(q).reduce((sum, v) => sum + v.price, 0);
    return { type: 'value', value: Math.max(0, this.#subscriptionPrice(oneTime, q) - total), percentage: 0 };
  }

  getCrossSellPrices() {
    return { price: 0, compareAtPrice: 0 };
  }

  /* ---------- Calculs (miroir de la Function) ---------- */

  #cardQuantity(card) {
    return parseInt(card.dataset.quantity, 10);
  }

  #selectedQuantity() {
    return this.#cardQuantity(this.getSelectedQuantity());
  }

  /** Parfums des `quantity` premiers pots (au-delà des choix : parfum du pot 1). */
  #selectedVariants(quantity = this.#selectedQuantity()) {
    return Array.from({ length: quantity }, (_, i) => this.#variant(this.picks[i] ?? this.picks[0]));
  }

  /** Ajuste la liste des pots à une quantité (on garde les choix existants, on complète au parfum du pot 1). */
  #resizePicks(quantity) {
    if (this.picks.length > quantity) this.picks.length = quantity;
    while (this.picks.length < quantity) this.picks.push(this.picks[0]);
  }

  #variant(id) {
    return this.variants.find((v) => String(v.id) === String(id)) ?? this.variants[0];
  }

  #subscription() {
    return this.closest('product-form')?.querySelector('subscription-component') ?? null;
  }

  #subscriptionSelected() {
    return Boolean(this.#subscription()?.isSubscriptionSelected?.());
  }

  /**
   * Réduction de la formule d'abonnement choisie (même si l'abonnement n'est pas coché),
   * lue sur l'option de fréquence sélectionnée du composant abonnement.
   * @returns {{type: string, value: number} | null}
   */
  #planAdjustment() {
    const sub = this.#subscription();
    const option = sub?.querySelector('select')?.selectedOptions?.[0] ?? sub?.querySelector('[data-ref="frequency-input"]:checked');
    if (!option?.dataset.adjustmentType) return null;
    return { type: option.dataset.adjustmentType, value: parseFloat(option.dataset.adjustmentValue) || 0 };
  }

  /** Prix abonnement d'un montant (centimes) : même formule que subscription.js. */
  #subscriptionPrice(base, quantity) {
    const adjustment = this.#planAdjustment();
    if (!adjustment) return base;
    switch (adjustment.type) {
      case 'percentage':
        return Math.round((base * (100 - adjustment.value)) / 100);
      case 'fixed_amount':
        return Math.max(0, base - adjustment.value * quantity);
      case 'price':
        return adjustment.value * quantity;
      default:
        return base;
    }
  }

  #sellingPlan() {
    if (!this.#subscriptionSelected()) return null;
    return this.closest('product-form')?.querySelector('[data-ref="selling-plan-input"]')?.value || null;
  }

  /** Pourcentage du palier atteint pour une quantité (mode paliers). */
  #tierPercentage(quantity) {
    return (this.config.paliers ?? [])
      .filter((tier) => quantity >= tier.quantite)
      .reduce((best, tier) => Math.max(best, tier.pourcentage), 0);
  }

  /**
   * Prix d'une quantité avec les parfums choisis (pots au-delà des lignes : parfum du pot 1).
   * En abonnement, la réduction de la formule s'applique d'abord, puis la remise quantité
   * (ordre Shopify), seulement si la config l'autorise (`abonnement: true`).
   * @param {number} quantity
   * @param {boolean} subscription Prix abonnement.
   * @returns {{total: number, base: number, compare: number, off: number, percentage: number, discounted: boolean}} centimes
   */
  #pricing(quantity, subscription = false) {
    const variants = this.#selectedVariants(quantity);
    const compare = Math.max(
      variants.reduce((sum, v) => sum + v.price, 0),
      variants.reduce((sum, v) => sum + v.compare, 0),
    );
    // Une ligne de panier par parfum, comme product-form.js (fusion par variante).
    // Shopify calcule la remise par unité, tronquée au centime (vérifié en panier réel).
    const lines = new Map();
    variants.forEach((v) => {
      const line = lines.get(v.id) ?? { unit: subscription ? this.#subscriptionPrice(v.price, 1) : v.price, qty: 0 };
      line.qty += 1;
      lines.set(v.id, line);
    });
    const base = [...lines.values()].reduce((sum, l) => sum + l.unit * l.qty, 0);
    const applies = !subscription || Boolean(this.config.abonnement);

    let off = 0;
    let percentage = 0;
    if (applies && this.config.mode === 'bogo') {
      const { achete, offert, pourcentage } = this.config.bogo;
      let remaining = Math.floor(quantity / (achete + offert)) * offert;
      [...lines.values()].sort((x, y) => x.unit - y.unit).forEach((l) => {
        const take = Math.min(remaining, l.qty);
        off += Math.floor((l.unit * pourcentage) / 100) * take;
        remaining -= take;
      });
      percentage = base ? Math.round((off / base) * 100) : 0;
    } else if (applies) {
      percentage = this.#tierPercentage(quantity);
      off = [...lines.values()].reduce((sum, l) => sum + Math.floor((l.unit * percentage) / 100) * l.qty, 0);
    }
    return { total: base - off, base, compare, off, percentage, discounted: off > 0 };
  }

  /* ---------- Rendu ---------- */

  #onChange() {
    this.#render();
    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  #onCardChange() {
    // La dernière carte repart toujours à sa quantité d'entrée.
    if (this.plusCard && this.getSelectedQuantity() === this.plusCard) this.plusCard.dataset.quantity = String(this.plusMin);
    this.#resizePicks(this.#selectedQuantity());
    this.#onChange();
  }

  #sameFlavor() {
    this.picks.fill(this.picks[0]);
    this.#onChange();
  }

  /** Dernière carte : un pot de plus de ce parfum (pas de plafond). */
  #addPot(id) {
    this.picks.push(String(id));
    this.plusCard.dataset.quantity = String(this.picks.length);
    this.#onChange();
  }

  /** Dernière carte : un pot de moins de ce parfum, sans descendre sous la quantité de la carte. */
  #removePot(id) {
    const index = this.picks.lastIndexOf(String(id));
    if (index < 0 || this.picks.length <= this.plusMin) return;
    this.picks.splice(index, 1);
    this.plusCard.dataset.quantity = String(this.picks.length);
    this.#onChange();
  }

  #onVariantUpdated(event) {
    const id = event?.detail?.resource?.id;
    if (!id || !this.variants.some((v) => String(v.id) === String(id))) return;
    this.picks.fill(String(id));
    this.#render();
    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  #render() {
    const subscription = this.#subscriptionSelected();
    const quantity = this.#selectedQuantity();

    this.cards.forEach((card) => {
      const q = this.#cardQuantity(card);
      const { total: shown, compare, percentage, discounted } = this.#pricing(q, subscription);
      const qty = card.querySelector('[data-ref="cq-qty"]');
      if (qty) qty.textContent = String(q);
      const count = card.querySelector('[data-ref="cq-count"]');
      if (count) count.textContent = `×${q}`;
      card.classList.toggle('cq-card--many', q > 4);
      const plus = card.querySelector('[data-ref="cq-plus"]');
      if (plus) plus.hidden = card === this.getSelectedQuantity() && q > this.plusMin;
      const save = card.querySelector('[data-ref="cq-save"]');
      const totalEl = card.querySelector('[data-ref="cq-total"]');
      const compareEl = card.querySelector('[data-ref="cq-compare"]');
      const sub = card.querySelector('[data-ref="cq-sub"]');

      totalEl.textContent = formatMoney(shown);
      compareEl.hidden = !(compare > shown);
      compareEl.textContent = formatMoney(compare);

      save.hidden = !discounted;
      if (discounted) {
        save.textContent = this.config.mode === 'bogo' ? this.#bogoLabel(q) : `−${percentage} %`;
      }

      const parts = [];
      if (this.monthsPerUnit) parts.push(`${q * this.monthsPerUnit} mois de cure`);
      parts.push(`${formatMoney(Math.round(shown / q))} le ${this.unit}`);
      if (this.dosesPerUnit) parts.push(`${formatMoney(Math.round(shown / (q * this.dosesPerUnit)))} la dose`);
      sub.textContent = parts.join(' · ');
      const gain = card.querySelector('[data-ref="cq-gain"]');
      gain.hidden = !discounted;
      gain.textContent = discounted ? `${formatMoney(compare - shown)} économisés` : '';

    });

    // Dernière carte : compteurs par parfum. Autres cartes : un menu par pot.
    const onPlus = this.getSelectedQuantity() === this.plusCard;
    if (this.counters) {
      this.counters.hidden = !onPlus;
      if (onPlus) this.plusCard.appendChild(this.counters);
      const counts = this.picks.reduce((acc, id) => acc.set(String(id), (acc.get(String(id)) ?? 0) + 1), new Map());
      this.counters.querySelectorAll('[data-ref="cq-counter"]').forEach((row) => {
        const n = counts.get(row.dataset.variantId) ?? 0;
        row.querySelector('[data-ref="cq-counter-value"]').textContent = String(n);
        const minus = row.querySelector('[data-ref="cq-minus"]');
        if (!row.classList.contains('cq-counter--off')) minus.disabled = n === 0 || this.picks.length <= this.plusMin;
      });
      const total = this.counters.querySelector('[data-ref="cq-counters-total"]');
      total.textContent = `Total : ${quantity} ${quantity > 1 ? this.units : this.unit}` + (quantity <= this.plusMin ? ` (minimum ${this.plusMin} sur cette offre)` : '');
    }

    this.selects.forEach((select, i) => {
      if (this.picks[i] && select.value !== String(this.picks[i])) select.value = String(this.picks[i]);
    });
    this.rows.forEach((row, i) => (row.hidden = i >= quantity));
    this.rows.forEach((row, i) => {
      const thumb = row.querySelector('[data-ref="cq-flavor-thumb"]');
      const v = this.#variant(this.selects[i].value);
      const src = v.swatch || v.image;
      if (src && thumb && thumb.src !== src) thumb.src = src;
    });
    const title = this.querySelector('[data-ref="cq-flavors-title"]');
    if (title) title.textContent = quantity > 1 ? title.dataset.many : title.dataset.single;
    if (this.flavors && !this.flavors.hasAttribute('hidden')) {
      if (onPlus) {
        this.flavors.remove();
      } else {
        this.getSelectedQuantity().appendChild(this.flavors);
        this.querySelector('[data-ref="cq-same"]').hidden = quantity < 2;
      }
    }
    this.#syncStackImages();
    const excluded = subscription && !this.config.abonnement;
    this.#renderHead(excluded);
    this.#renderNudge(excluded, quantity);
  }

  #bogoLabel(q) {
    const { achete, offert, pourcentage } = this.config.bogo;
    const n = Math.floor(q / (achete + offert)) * offert;
    if (pourcentage === 100) return n > 1 ? `${n} offerts` : '1 offert';
    return n > 1 ? `${n} à −${pourcentage} %` : `${achete + 1}e à −${pourcentage} %`;
  }

  #syncStackImages() {
    const images = this.#selectedVariants(4).map((v) => v.image).filter(Boolean);
    this.cards.forEach((card) => {
      card.querySelectorAll('.cq-card__pot img').forEach((img, i) => {
        const src = images[i] ?? images[0];
        if (src && img.src !== src) img.src = src;
      });
    });
  }

  #renderHead(excluded) {
    const headline = this.querySelector('[data-ref="cq-headline"]');
    if (excluded) {
      headline.textContent = '';
      return;
    }
    if (this.config.mode === 'bogo') {
      const { achete, pourcentage } = this.config.bogo;
      headline.textContent = pourcentage === 100 ? `${achete + 1}e ${this.unit} offert` : `${achete + 1}e ${this.unit} à −${pourcentage} %`;
      return;
    }
    const max = Math.max(...(this.config.paliers ?? []).map((t) => t.pourcentage));
    headline.textContent = `Jusqu'à −${max} %`;
  }

  /** Progression vers le prochain palier : « Plus qu'1 pot pour passer à −15 % ». */
  #renderNudge(excluded, quantity) {
    const nudge = this.querySelector('[data-ref="cq-nudge"]');
    const text = this.querySelector('[data-ref="cq-nudge-text"]');
    const pips = this.querySelector('[data-ref="cq-pips"]');
    // Jalons = quantité d'entrée de chaque carte (la dernière commence à son minimum).
    const steps = this.cards.map((card) => (card === this.plusCard ? this.plusMin : this.#cardQuantity(card)));

    if (excluded) {
      nudge.hidden = false;
      pips.innerHTML = '';
      text.textContent = `Remise quantité réservée à l'achat unique. L'abonnement garde sa propre réduction.`;
      return;
    }

    pips.innerHTML = steps.map((q) => `<i class="${q <= quantity ? 'is-on' : ''}"></i>`).join('');
    const next = steps.find((q) => q > quantity);
    if (!next) {
      nudge.hidden = false;
      text.textContent = this.config.mode === 'bogo' ? 'Offre maximale atteinte' : `Meilleur prix atteint : −${this.#tierPercentage(quantity)} % sur chaque ${this.unit}`;
      return;
    }
    const missing = next - quantity;
    const gain = this.config.mode === 'bogo' ? this.#bogoLabel(next) : `−${this.#tierPercentage(next)} %`;
    nudge.hidden = false;
    const count = missing === 1 ? `qu'un ${this.unit}` : `que ${missing} ${this.units}`;
    text.textContent = `Plus ${count} pour passer à ${gain}`;
  }
}

if (!customElements.get('coalition-quantite')) {
  customElements.define('coalition-quantite', CoalitionQuantite);
}
